import React from 'react';
import PropTypes from 'prop-types';

// Components
import StatusChip, { connectionChip } from './StatusChip';

// Constants
import { isNotNull, lowBatteryPercent } from '../constants';
import { T, FONT_MONO, INDICATOR_TEXT } from '../constants/tokens';

// Icons
import Hallway from '../images/customIcons/Hallway.png';
import Room from '../images/customIcons/Room.png';
import Door from '../images/customIcons/Door.png';

function getIcon(hubType) {
  switch (hubType) {
    case 'Hallway':
      return Hallway;
    case 'Room':
      return Room;
    case 'Door':
      return Door;
    default:
      return;
  }
}

/** Connection and firmware, stacked. Used in both layouts. */
function StatusLines({ hub, align = 'flex-end' }) {
  const connection = connectionChip(hub);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: align, gap: 2 }}>
      {connection && <StatusChip tone={connection.tone} label={connection.label} />}
      {isNotNull(hub.getFirmwareVersion()) &&
        <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: T.mutedSoft }}>
          v{hub.getFirmwareVersion()}
        </span>}
    </div>
  );
}

StatusLines.propTypes = {
  hub: PropTypes.object.isRequired,
  align: PropTypes.string
};

/**
 * One hub inside a detail panel, as a card: what kind of hub it is, its serial
 * number, whether it is connected and how much battery it has.
 *
 * The first v7 pass flattened the old tile into a hairline row, which pushed
 * the type and the battery into small print. In a room's panel those are the
 * point: which of this room's hubs is the door hub, and is it alive.
 */
function PanelCard({ hub }) {
  const connected = hub.getIsConnected();
  const battery = hub.getBatteryLevel();
  const low = battery != null && battery < lowBatteryPercent();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 12,
        marginTop: 8,
        background: T.surfaceSoft,
        border: '1px solid ' + T.hairline,
        borderRadius: 6
      }}
    >
      <img
        src={getIcon(hub.getHubType())}
        alt=''
        style={{ width: 36, height: 36, flex: 'none', objectFit: 'contain' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: T.ink }}>
          {hub.getHubType() ? hub.getHubType() + ' hub' : 'Hub'}
        </div>
        <div
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12,
            lineHeight: '16px',
            color: T.muted,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {hub.getSerialNumber()}
          {isNotNull(hub.getFirmwareVersion()) && ' · v' + hub.getFirmwareVersion()}
        </div>
      </div>
      <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
        <StatusChip tone={connected ? 'good' : 'bad'} label={connected ? 'Connected' : 'Not connected'} />
        {battery != null &&
          <span style={{ fontFamily: FONT_MONO, fontSize: 12, lineHeight: '16px', color: low ? INDICATOR_TEXT.warning : T.body }}>
            Battery {Math.floor(battery)}%
          </span>}
      </div>
    </div>
  );
}

PanelCard.propTypes = {
  hub: PropTypes.object.isRequired
};

export default function HubStatusWidget({ style = {}, hub, hubs, isInfoPanel = false, showLabel = false }) {
  const all = hubs ?? (hub ? [hub] : []);
  if (all.length === 0) return null;

  if (isInfoPanel) {
    return (
      <div style={{ margin: '24px 0', ...style }}>
        <div style={{ fontFamily: FONT_MONO, fontSize: 12, fontWeight: 500, lineHeight: '16px', textTransform: 'uppercase', color: T.body }}>
          Hubs · {all.length}
        </div>
        {all.map((h, index) => <PanelCard key={index} hub={h} />)}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, ...style }}>
      {showLabel &&
        <span
          style={{
            fontFamily: FONT_MONO,
            fontSize: 12,
            fontWeight: 500,
            textTransform: 'uppercase',
            color: T.muted
          }}
        >
          Hub status
        </span>}
      {all.map((h, index) => <StatusLines key={index} hub={h} />)}
    </div>
  );
}

HubStatusWidget.propTypes = {
  hub: PropTypes.object,
  hubs: PropTypes.array,
  isInfoPanel: PropTypes.bool,
  showLabel: PropTypes.bool,
  style: PropTypes.object
};
